import { useState } from 'react';
import { X, Minus, Plus, ShoppingBag, Eye, Star, Award, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { cleanTitle } from './ProductCard';

const safeNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/* ── Colour swatch map (shared with ProductCard) ── */
const SWATCH_MAP = {
  'Black': '#000', 'White': '#FFF', 'Pink': '#F4A4B8', 'Light Pink': '#FFD1DC',
  'Pale Pink': '#FADADD', 'Candy Pink': '#E4717A', 'Theatrical Pink': '#F77FBE',
  'Ballet Pink Leather': '#E8B4B8', 'Blush': '#DE98AB', 'Rose': '#C67D7D',
  'Coral': '#F08080', 'Raspberry': '#C4295B', 'Red': '#CC2936', 'Burgundy': '#800020',
  'Black Cherry': '#49111C', 'Terracotta': '#C06040', 'Salted Caramel': '#C49560',
  'Caramel Blonde': '#C49560', 'Chocolate': '#5C3317', 'Brown': '#8B6F47',
  'Light Brown': '#B68D66', 'Med Brown': '#9A6F48', 'Wood': '#8B7355', 'Tan': '#D2B48C',
  'Taupe': '#B8A99A', 'Sand': '#D4C5A9', 'Nude': '#E3C9A8', 'Light Nude': '#F0DFC8',
  'Beige': '#F5E1C8', 'Natural': '#EEDCC8', 'Oat': '#E9DDD0', 'Cream': '#FFFDD0',
  'Milky Coconut': '#F5F0E8', 'Smooth Vanilla': '#F5E6CC', 'Blonde': '#F5DEB3',
  'Moonstone': '#BCC5CE', 'Slate': '#708090', 'Marl': '#A8A8A8',
  'Navy': '#1A2744', 'Deep Navy': '#0F1A2E', 'Royal': '#2B3A8E', 'Haze Blue': '#99B5C8',
  'Forest Green': '#2D5A27', 'Olive': '#708238', 'Soft Violet': '#A68BB5',
  'Leopard': 'pattern', 'Clear': 'transparent', 'clear': 'transparent',
};
const LIGHT_COLOURS = new Set(['White', 'Cream', 'Natural', 'Oat', 'Milky Coconut', 'Smooth Vanilla', 'Beige', 'Light Nude', 'Blonde', 'Clear', 'clear']);

const ColourSwatch = ({ colour, size = 14 }) => {
  const hex = SWATCH_MAP[colour];
  if (!hex) return null;
  if (hex === 'transparent') {
    return <span className="rounded-full border-2 border-dashed border-[#ccc] flex-shrink-0" style={{ width: size, height: size }} />;
  }
  if (hex === 'pattern') {
    return (
      <span
        className="rounded-full flex-shrink-0 border border-[#ddd]"
        style={{ width: size, height: size, background: 'radial-gradient(circle at 30% 30%, #3B2507 1.5px, transparent 1.5px), radial-gradient(circle at 70% 60%, #3B2507 1.5px, transparent 1.5px), linear-gradient(135deg, #D4A952, #C4913A)' }}
      />
    );
  }
  const light = LIGHT_COLOURS.has(colour);
  return (
    <span
      className={`rounded-full flex-shrink-0 ${light ? 'border border-[#ddd]' : ''}`}
      style={{ width: size, height: size, backgroundColor: hex }}
    />
  );
};

const StarRating = ({ rating }) => (
  <div className="flex gap-px">
    {[1, 2, 3, 4, 5].map(s => (
      <Star key={s} className={`w-4 h-4 ${s <= Math.round(rating) ? 'fill-[#DEA193] text-[#DEA193]' : 'fill-none text-[#E5D8D0]'}`} />
    ))}
  </div>
);

/* ── Image Gallery ── */
const QuickViewGallery = ({ images, product, imgIndex, setImgIndex }) => (
  <div className="relative bg-[#F5F5F5]">
    <div className="aspect-[4/5] overflow-hidden">
      <img
        src={images[imgIndex] || ''}
        alt={product.name}
        className="w-full h-full object-contain"
        style={!images[imgIndex] ? { display: 'none' } : {}}
      />
      {!images[imgIndex] && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#F0EFE7]">
          <div className="w-14 h-14 mb-3 rounded-full bg-[#E5D8D0]/30 flex items-center justify-center">
            <svg className="w-7 h-7 text-[#C4B5A8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
          </div>
          <span className="text-[11px] tracking-[0.1em] text-[#B0A89E] uppercase">Image Coming Soon</span>
        </div>
      )}
    </div>
    {images.length > 1 && (
      <div className="flex gap-2 p-3 justify-center">
        {images.slice(0, 4).map((img, i) => (
          <button
            key={`thumb-${i}`}
            onClick={() => setImgIndex(i)}
            className={`w-12 h-15 border-2 overflow-hidden bg-[#F5F5F5] ${i === imgIndex ? 'border-[#DEA193]' : 'border-transparent'}`}
          >
            <img src={img} alt="" className="w-full h-full object-contain" />
          </button>
        ))}
      </div>
    )}
    {product.bestseller && (
      <span className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-[#000] text-white text-[10px] tracking-[0.15em] font-semibold">
        <Award className="w-3 h-3" /> Bestseller
      </span>
    )}
  </div>
);

/* ── Size & Colour Options ── */
const QuickViewOptions = ({ sizes, colors, selectedSize, setSelectedSize, selectedColor, setSelectedColor, getSizeStock }) => (
  <>
    {sizes.length > 0 && (
      <div className="mb-4">
        <p className="text-xs tracking-[0.15em] text-[#737373] mb-2">Size</p>
        <div className="flex flex-wrap gap-2" data-testid="quick-view-sizes">
          {sizes.map(size => {
            const stock = getSizeStock(size);
            const oos = stock <= 0;
            return (
              <button
                key={size}
                onClick={() => !oos && setSelectedSize(size)}
                disabled={oos}
                className={`min-w-[44px] px-3 py-2 border text-sm transition-colors relative ${
                  selectedSize === size
                    ? 'border-[#000] bg-[#000] text-white'
                    : oos
                    ? 'border-[#E5D8D0] text-[#ccc] cursor-not-allowed line-through'
                    : 'border-[#E5D8D0] hover:border-[#000]'
                }`}
                data-testid={`quick-view-size-${size}`}
              >
                {size}
                {stock > 0 && stock <= 5 && (
                  <span className="absolute -top-1.5 -right-1.5 w-2 h-2 bg-yellow-400 rounded-full" title={`${stock} left`} />
                )}
              </button>
            );
          })}
        </div>
      </div>
    )}
    {colors.length > 0 && (
      <div className="mb-4">
        <p className="text-xs tracking-[0.15em] text-[#737373] mb-2">Colour</p>
        <div className="flex flex-wrap gap-2" data-testid="quick-view-colours">
          {colors.map(color => (
            <button
              key={color}
              onClick={() => setSelectedColor(color)}
              className={`flex items-center gap-2 px-3 py-2 border text-sm transition-colors ${
                selectedColor === color
                  ? 'border-[#000] bg-[#000] text-white'
                  : 'border-[#E5D8D0] hover:border-[#000]'
              }`}
              data-testid={`quick-view-color-${color}`}
            >
              <ColourSwatch colour={color} size={selectedColor === color ? 12 : 14} />
              {color}
            </button>
          ))}
        </div>
      </div>
    )}
  </>
);

/* ── Quantity + Actions ── */
const QuickViewActions = ({ quantity, setQuantity, handleAddToCart, adding, isOutOfStock, product, onClose }) => (
  <div className="mt-auto pt-4 space-y-3">
    <div className="flex items-center gap-3">
      <div className="flex items-center border border-[#E5D8D0]">
        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 hover:bg-[#F7F7EB] transition-colors" data-testid="quick-view-qty-minus">
          <Minus className="w-4 h-4" />
        </button>
        <span className="px-4 py-2 text-sm font-medium min-w-[40px] text-center" data-testid="quick-view-qty">{quantity}</span>
        <button onClick={() => setQuantity(quantity + 1)} className="p-2 hover:bg-[#F7F7EB] transition-colors" data-testid="quick-view-qty-plus">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <button
        onClick={handleAddToCart}
        disabled={adding || isOutOfStock}
        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#000] text-white text-sm font-medium tracking-wider hover:bg-[#222] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="quick-view-add-to-cart"
      >
        <ShoppingBag className="w-4 h-4" />
        {isOutOfStock ? 'Out of Stock' : adding ? 'Adding...' : 'Add to Cart'}
      </button>
    </div>
    <Link
      to={`/product/${product.id}`}
      onClick={onClose}
      className="flex items-center justify-center gap-1.5 text-xs text-[#737373] hover:text-[#000] transition-colors py-2"
      data-testid="quick-view-full-details"
    >
      <ExternalLink className="w-3.5 h-3.5" /> View Full Details
    </Link>
  </div>
);

/* ── Main Modal ── */
export const QuickViewModal = ({ product, onClose }) => {
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  if (!product) return null;

  const sizes = product.sizes || [];
  const colors = product.colors || [];
  const images = product.images || [];
  const stockBySize = product.stock_by_size || {};

  const getSizeStock = (size) => stockBySize[size] !== undefined ? stockBySize[size] : product.stock;

  const handleAddToCart = async () => {
    if (sizes.length > 0 && !selectedSize) { toast.error('Please select a size'); return; }
    if (colors.length > 0 && !selectedColor) { toast.error('Please select a colour'); return; }
    setAdding(true);
    const result = await addToCart(product.id, quantity, selectedSize || null, selectedColor || null);
    if (result.success) { toast.success(`${product.name} added to cart`); onClose(); }
    else { toast.error(result.error); }
    setAdding(false);
  };

  const isOutOfStock = sizes.length > 0 && selectedSize
    ? getSizeStock(selectedSize) <= 0
    : Object.keys(stockBySize).length > 0
      ? Object.values(stockBySize).reduce((a, b) => safeNum(a) + safeNum(b), 0) <= 0
      : product.stock <= 0;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center" data-testid="quick-view-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200" data-testid="quick-view-content">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 bg-white/90 border border-[#E5D8D0] hover:border-[#000] transition-colors" data-testid="quick-view-close">
          <X className="w-4 h-4" />
        </button>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <QuickViewGallery images={images} product={product} imgIndex={imgIndex} setImgIndex={setImgIndex} />
          <div className="p-6 flex flex-col">
            <p className="text-xs tracking-[0.2em] text-[#737373] mb-1">{product.brand || product.category}</p>
            <h2 className="text-2xl text-[#000] mb-2" data-testid="quick-view-name">{cleanTitle(product.name, product.brand, product.colors)}</h2>
            {product.review_count > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <StarRating rating={product.average_rating} />
                <span className="text-xs text-[#999]">({product.review_count})</span>
              </div>
            )}
            {product.on_sale && product.sale_price && product.sale_price < product.price ? (
              <div className="flex items-center gap-2.5 mb-4" data-testid="quick-view-price">
                <span className="text-xl font-medium text-[#DEA193]">£{safeNum(product.sale_price).toFixed(2)}</span>
                <span className="text-sm text-[#999] line-through">£{safeNum(product.price).toFixed(2)}</span>
                <span className="text-xs font-semibold text-[#DEA193]">-{Math.round((1 - product.sale_price / product.price) * 100)}%</span>
              </div>
            ) : (
              <p className="text-xl font-medium text-[#000] mb-4" data-testid="quick-view-price">£{safeNum(product.price).toFixed(2)}</p>
            )}
            <div className="text-sm text-[#737373] mb-6 line-clamp-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description || '') }} />
            <QuickViewOptions
              sizes={sizes} colors={colors}
              selectedSize={selectedSize} setSelectedSize={setSelectedSize}
              selectedColor={selectedColor} setSelectedColor={setSelectedColor}
              getSizeStock={getSizeStock}
            />
            <QuickViewActions
              quantity={quantity} setQuantity={setQuantity}
              handleAddToCart={handleAddToCart} adding={adding}
              isOutOfStock={isOutOfStock} product={product} onClose={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const QuickViewButton = ({ onClick }) => (
  <button
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
    className="absolute top-3 right-12 p-2 bg-white/90 backdrop-blur-sm border border-[#E5D8D0] hover:border-[#DEA193] transition-all opacity-0 group-hover:opacity-100 z-10"
    data-testid="quick-view-btn"
    aria-label="Quick view"
  >
    <Eye className="w-4 h-4 text-[#737373]" />
  </button>
);
